/**
 * Інвентаризаційний опис необоротних активів — офіційна форма за Наказом
 * МФУ від 17.06.2015 № 572 (2026-08-31, зразок власника з 1С: шапка
 * «ЗАТВЕРДЖЕНО», установа+ЄДРПОУ, розписка МВО, 16-графна таблиця,
 * підсумки прописом, підписи комісії). Канон .print-page + PrintActions;
 * альбомна орієнтація (таблиця широка).
 * Графи 11–15 «за даними бухгалтерського обліку» беруться зі snapshot'ів
 * документа, для непроведеного — з поточного реєстру НА.
 */
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { inventory } from '@/api/asset_inventory_sale';
import { assetsRegistry } from '@/api/assetsRegistry';
import { orgs } from '@/api/catalogs';
import { departmentsApi, employees as employeesApi } from '@/api/hr';
import PrintActions from '@/shared/components/PrintActions';
import { amountToWordsUAFull, intToWordsUA } from '@/utils/numberToWordsUA';

const fmt2 = (v: number | null | undefined) => v == null ? ''
  : v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (v: string | null | undefined) => v ? new Date(v).toLocaleDateString('uk-UA') : '';
/** «29 вересня 2023 р.» — дати в тексті бланка. */
const MONTHS_GEN = ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'];
const fmtDLong = (v: string | null | undefined) => {
  if (!v) return '';
  const d = new Date(v);
  return `${d.getDate()} ${MONTHS_GEN[d.getMonth()]} ${d.getFullYear()} р.`;
};

function SignRow({ role, name }: { role?: string; name?: string }) {
  return (
    <div className="fai-sign-row">
      <span className="fai-line" style={{ width: '32%' }}>{role ?? ''}</span>
      <span className="fai-line" style={{ width: '22%' }} />
      <span className="fai-line" style={{ width: '32%' }}>{name ?? ''}</span>
      <div className="fai-sign-caps">
        <span style={{ width: '32%' }}>(посада)</span>
        <span style={{ width: '22%' }}>(підпис)</span>
        <span style={{ width: '32%' }}>(ініціали, прізвище)</span>
      </div>
    </div>
  );
}

function WordsLine({ label, words }: { label: string; words: string }) {
  return (
    <div style={{ margin: '2px 0 6px 1.5rem' }}>
      {label}{' '}
      <span className="fai-line" style={{ minWidth: '55%' }}><b>{words}</b></span>
      <div style={{ marginLeft: '40%', fontSize: '7pt' }}>(прописом)</div>
    </div>
  );
}

export default function FixedAssetInventoryPrintPage() {
  const { id } = useParams<{ id: string }>();
  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['fa-inventory', id],
    queryFn: () => inventory.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const regQ = useQuery({
    queryKey: ['assets-registry-all', d?.organization_id],
    // Бекенд обмежує limit ≤ 500 — тягнемо реєстр сторінками до total.
    queryFn: async () => {
      const first = await assetsRegistry.list({ organization_id: d!.organization_id, limit: 500 });
      const items = [...first.items];
      while (items.length < first.total) {
        const page = await assetsRegistry.list({
          organization_id: d!.organization_id, limit: 500, offset: items.length });
        if (page.items.length === 0) break;
        items.push(...page.items);
      }
      return { ...first, items };
    },
    enabled: !!d?.organization_id,
  });
  const depsQ = useQuery({
    queryKey: ['departments-items', d?.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: d!.organization_id, limit: 500 }).then((r) => r.items),
    enabled: !!d?.organization_id,
  });
  const empsQ = useQuery({
    queryKey: ['employees-items', d?.organization_id],
    queryFn: () => employeesApi.list({ organization_id: d!.organization_id, limit: 500 }).then((r) => r.items),
    enabled: !!d?.organization_id,
  });
  const regById = useMemo(
    () => new Map((regQ.data?.items ?? []).map((a) => [a.id, a])),
    [regQ.data]);

  if (!d || !org) return <div style={{ padding: '2rem' }}>Завантаження…</div>;

  const deps = Array.isArray(depsQ.data) ? depsQ.data : [];
  const emps = Array.isArray(empsQ.data) ? empsQ.data : [];
  const deptName = deps.find((x) => x.id === d.department_id)?.name;
  const empName = (eid: string | null | undefined) =>
    emps.find((x) => x.id === eid)?.full_name ?? '';
  const mvoName = d.location_kind === 'EMPLOYEE' ? empName(d.employee_id) : '';
  const chairName = empName(d.commission_chair_employee_id) || (org.director_name ?? '');
  const storagePlace = d.location_kind === 'DEPARTMENT' ? (deptName ?? '')
    : d.location_kind === 'EMPLOYEE' ? mvoName : 'вся організація';

  // Рядки таблиці: snapshot (проведений) або поточний реєстр НА (опис).
  const rows = d.items.map((it) => {
    const aid = it.fixed_asset_id ?? it.intangible_asset_id ?? it.low_value_asset_id ?? '';
    const a = regById.get(aid);
    const num = (s: string | null | undefined) => {
      const n = Number(s);
      return s != null && s !== '' && Number.isFinite(n) ? n : null;
    };
    const cost = num(it.primary_cost_snapshot) ?? num(a?.primary_cost) ?? 0;
    const depr = num(it.accumulated_depreciation_snapshot) ?? num(a?.accumulated_depreciation) ?? 0;
    const book = num(it.book_value_snapshot) ?? num(a?.residual_value) ?? (cost - depr);
    const found = it.actual_status !== 'MISSING';
    const notes = [
      it.actual_status === 'MISSING' ? 'відсутнє' : '',
      it.actual_status === 'FOUND_WRONG_LOCATION' ? 'не за місцем' : '',
      it.item_note ?? '',
    ].filter(Boolean).join('; ');
    return { it, a, cost, depr, book, found, notes };
  });
  const subaccounts = [...new Set(rows.map((r) => r.a?.account_code).filter(Boolean))]
    .sort().join(', ');
  const factCount = rows.filter((r) => r.found).length;
  // Гр.9 «фактична вартість» лишається порожньою (як у 1С-зразку власника,
  // 2026-08-31): комісія фіксує факт кількістю, вартість — графи бух.обліку.
  const bookCost = rows.reduce((s, r) => s + r.cost, 0);
  const bookDepr = rows.reduce((s, r) => s + r.depr, 0);
  const bookBal = rows.reduce((s, r) => s + r.book, 0);

  return (
    <div className="print-page fai-print" style={{
      maxWidth: 1150, margin: '0 auto', padding: '1.2rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        @media print { @page { size: A4 landscape; margin: 1cm; } }
        .fai-print .fai-line { display: inline-block; border-bottom: 1px solid #000; min-width: 120px; text-align: center; padding: 0 6px; }
        .fai-print .fai-caption { font-size: 7pt; text-align: center; }
        .fai-table { width: 100%; border-collapse: collapse; margin: 0.4rem 0; font-size: 8pt; line-height: 1.15; }
        .fai-table th, .fai-table td { border: 1px solid #000; padding: 1px 3px; vertical-align: middle; }
        .fai-table th { text-align: center; font-weight: 600; font-size: 7.5pt; }
        .fai-sign-row { margin: 10px 0 2px; }
        .fai-sign-row .fai-line { margin-right: 2%; }
        .fai-sign-caps { display: flex; font-size: 7pt; }
        .fai-sign-caps span { margin-right: 2%; text-align: center; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.fixed_asset_inventory',
        documentId: d.id,
        filename: `Інвентаризаційний опис НА ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ width: '46%' }}>
          <div style={{ borderBottom: '1px solid #000', textAlign: 'center', fontWeight: 600, fontSize: '9pt' }}>
            {org.full_name || org.name}
            {org.vat_number ? `, ІПН ${org.vat_number}` : org.inn ? `, ІПН ${org.inn}` : ''}
            {org.legal_address ? `, ${org.legal_address}` : ''}
          </div>
          <div className="fai-caption">(установа)</div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center' }}>
            <span>Ідентифікаційний код за ЄДРПОУ</span>
            <span style={{ border: '1px solid #000', padding: '1px 16px', marginLeft: 8, fontWeight: 600 }}>
              {org.edrpou ?? ''}
            </span>
          </div>
        </div>
        <div style={{ fontSize: '9pt', textAlign: 'left', width: 260 }}>
          ЗАТВЕРДЖЕНО<br />
          Наказ Міністерства фінансів України<br />
          17 червня 2015 № 572
        </div>
      </div>

      <div style={{ textAlign: 'center', margin: '10px 0 6px' }}>
        <div style={{ fontWeight: 700, fontSize: '11pt' }}>ІНВЕНТАРИЗАЦІЙНИЙ ОПИС</div>
        <div style={{ fontWeight: 700 }}>необоротних активів</div>
        <div style={{ fontSize: '7.5pt' }}>
          (основні засоби, нематеріальні активи, інші необоротні матеріальні активи, капітальні інвестиції)
        </div>
        <div style={{ marginTop: 4 }}>
          <span className="fai-line" style={{ minWidth: 220 }}><b>{fmtDLong(d.document_date)}</b></span>
          <div className="fai-caption">(дата складання)</div>
        </div>
      </div>

      <div style={{ lineHeight: 1.5 }}>
        На підставі розпорядчого документа{' '}
        <span className="fai-line" style={{ minWidth: 320 }}>{d.inventory_reason ?? ''}</span>{' '}
        виконано зняття фактичних залишків основних засобів, нематеріальних активів, інших
        необоротних матеріальних активів, капітальних інвестицій, які обліковуються на
        субрахунку(ах){' '}
        <span className="fai-line" style={{ minWidth: 160 }}>{subaccounts}</span>{' '}
        та зберігаються{' '}
        <span className="fai-line" style={{ minWidth: 200 }}>{storagePlace}</span>
        <br />
        станом на <span className="fai-line" style={{ minWidth: 180 }}><b>{fmtDLong(d.document_date)}</b></span>
      </div>

      <div style={{ textAlign: 'center', fontWeight: 700, margin: '8px 0 4px' }}>Розписка</div>
      <div style={{ lineHeight: 1.4 }}>
        До початку проведення інвентаризації всі видаткові та прибуткові документи на
        необоротні активи здано в бухгалтерську службу і всі необоротні активи, що надійшли
        на мою відповідальність, оприбутковано, а ті, що вибули, списано.
      </div>
      <div style={{ marginTop: 4 }}>Матеріально відповідальна особа:</div>
      <SignRow name={mvoName} />

      <div style={{ margin: '6px 0' }}>
        Інвентаризація: розпочата <b>{fmtDLong(d.inventory_period_start)}</b>{' '}
        закінчена <b>{fmtDLong(d.inventory_period_end)}</b>
      </div>

      <div>При інвентаризації встановлено таке:</div>
      <table className="fai-table">
        <thead>
          <tr>
            <th rowSpan={3} style={{ width: 26 }}>№<br />з/п</th>
            <th rowSpan={3}>Найменування,<br />стисла характеристика<br />та призначення об'єкта</th>
            <th rowSpan={3} style={{ width: 90 }}>Рік випуску<br />(будівництва) чи дата<br />придбання (введення<br />в експлуатацію)</th>
            <th colSpan={3}>Номер</th>
            <th rowSpan={3} style={{ width: 36 }}>Од.<br />вимір.</th>
            <th colSpan={2}>Фактична наявність</th>
            <th rowSpan={3} style={{ width: 40 }}>Відмітка<br />про<br />вибуття</th>
            <th colSpan={5}>За даними бухгалтерського обліку*</th>
            <th rowSpan={3} style={{ width: 70 }}>Інші<br />відомості</th>
          </tr>
          <tr>
            <th rowSpan={2} style={{ width: 72 }}>інвентарний /<br />номенклатурний</th>
            <th rowSpan={2} style={{ width: 56 }}>заводський</th>
            <th rowSpan={2} style={{ width: 50 }}>паспорта</th>
            <th rowSpan={2} style={{ width: 46 }}>кількість</th>
            <th rowSpan={2} style={{ width: 78 }}>первісна<br />(переоцінена)<br />вартість</th>
            <th rowSpan={2} style={{ width: 46 }}>кількість</th>
            <th rowSpan={2} style={{ width: 78 }}>первісна<br />(переоцінена)<br />вартість</th>
            <th rowSpan={2} style={{ width: 78 }}>сума зносу<br />(накопиченої<br />амортизації)</th>
            <th rowSpan={2} style={{ width: 78 }}>балансова<br />вартість</th>
            <th rowSpan={2} style={{ width: 48 }}>строк<br />корисного<br />використання</th>
          </tr>
          <tr />
          <tr>
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16']
              .map((n) => <th key={n} style={{ fontWeight: 400 }}>{n}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ it, a, cost, depr, book, found, notes }) => (
            <tr key={it.id ?? it.line_number}>
              <td style={{ textAlign: 'center' }}>{it.line_number}</td>
              <td>{a?.name ?? '—'}</td>
              <td style={{ textAlign: 'center' }}>{fmtD(a?.commissioning_date)}</td>
              <td style={{ textAlign: 'center' }}>{a?.inventory_number ?? ''}</td>
              <td />
              <td />
              <td style={{ textAlign: 'center' }}>шт</td>
              <td style={{ textAlign: 'center' }}>{found ? (a?.quantity ?? 1) : ''}</td>
              <td />
              <td />
              <td style={{ textAlign: 'center' }}>{a?.quantity ?? 1}</td>
              <td style={{ textAlign: 'right' }}>{fmt2(cost)}</td>
              <td style={{ textAlign: 'right' }}>{fmt2(depr)}</td>
              <td style={{ textAlign: 'right' }}>{fmt2(book)}</td>
              <td style={{ textAlign: 'center' }}>{a?.useful_life_months ?? ''}</td>
              <td>{notes}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 700 }}>
            <td>Разом</td>
            <td style={{ textAlign: 'center' }}>Х</td>
            <td style={{ textAlign: 'center' }}>Х</td>
            <td style={{ textAlign: 'center' }}>Х</td>
            <td style={{ textAlign: 'center' }}>Х</td>
            <td style={{ textAlign: 'center' }}>Х</td>
            <td style={{ textAlign: 'center' }}>Х</td>
            <td style={{ textAlign: 'center' }}>{factCount}</td>
            <td />
            <td />
            <td style={{ textAlign: 'center' }}>{rows.length}</td>
            <td style={{ textAlign: 'right' }}>{fmt2(bookCost)}</td>
            <td style={{ textAlign: 'right' }}>{fmt2(bookDepr)}</td>
            <td style={{ textAlign: 'right' }}>{fmt2(bookBal)}</td>
            <td style={{ textAlign: 'center' }}>Х</td>
            <td style={{ textAlign: 'center' }}>Х</td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: 6 }}>Разом за описом:</div>
      <WordsLine label="а) кількість порядкових номерів" words={intToWordsUA(rows.length)} />
      <WordsLine label="б) загальна кількість одиниць (фактично)" words={intToWordsUA(factCount, true)} />
      <WordsLine label="в) вартість фактична" words={amountToWordsUAFull(0)} />
      <WordsLine label="г) загальна кількість одиниць за даними бухгалтерського обліку" words={intToWordsUA(rows.length, true)} />
      <WordsLine label="ґ) вартість за даними бухгалтерського обліку" words={amountToWordsUAFull(bookCost)} />

      {d.findings_summary && (
        <div style={{ margin: '6px 0' }}>Висновки комісії: {d.findings_summary}</div>
      )}

      <div style={{ marginTop: 8 }}>Голова комісії</div>
      <SignRow name={chairName} />
      <div style={{ marginTop: 6 }}>Члени комісії</div>
      <SignRow name={d.commission_members_text ?? ''} />
      <SignRow />

      <div style={{ marginTop: 10, lineHeight: 1.4 }}>
        Усі цінності, пронумеровані в цьому інвентаризаційному описі з № 1 до № {rows.length},
        перевірено комісією в натурі в моїй присутності та внесено в опис. У зв'язку з цим
        претензій до інвентаризаційної комісії не маю. Цінності, перелічені в описі,
        знаходяться на моєму відповідальному зберіганні.
      </div>
      <div style={{ marginTop: 4 }}>Матеріально відповідальна особа:</div>
      <div>«___» _____________________ 20___ р.</div>
      <SignRow name={mvoName} />

      <div style={{ marginTop: 8 }}>Інформацію за даними бухгалтерського обліку вніс:</div>
      <SignRow name={org.chief_accountant ?? ''} />
      <div style={{ marginTop: 6 }}>Вказані в цьому описі дані перевірив:</div>
      <div>«___» _____________________ 20___ р.</div>
      <SignRow />

      <div style={{ marginTop: 8, fontSize: '7.5pt' }}>
        * Графи 11–15 заповнюються бухгалтерською службою.
      </div>
    </div>
  );
}
